/**
 * Turning stored messages into the rows the thread draws: which kinds become a
 * row at all, and how a group event is phrased.
 *
 * Pure, and kept out of the connected view so it can be tested without pulling
 * the directory graph in (as `hooks/peerProfiles.ts` is, for the same reason).
 */
import { canRetract } from "./canRetract"
import type { EventMessage, Message, PeerProfile } from "../../types"
import type { SharedItem } from "../../../components/SharedProductCard"
import type { Jid } from "../../xmpp/jid"
import { isOwnMessage } from "../../xmpp/stanzas/message"
import type { ThreadRow } from "../Thread"
import type { Translate } from "./translate"

type ThreadRowsContext = {
  selfJid: Jid | undefined
  resolveHandle: (handle: string) => PeerProfile
  retractionWindowHours: number | undefined
  t: Translate
  /** Retractability expires on the clock, so the caller passes a ticking one. */
  now: number
  /**
   * Resolved shared-item metadata (FAME-453), keyed by id. Omitted or `undefined`
   * while the batch lookup is in flight; an id absent from it means the caller
   * has no access, which the card renders as such.
   */
  resolvedShares?: Map<string, SharedItem>
  /** The resolve lookup failed — the share card falls back to the deep link. */
  sharesFailed?: boolean
}

/**
 * A retraction is the one kind that never becomes a row of its own: it names
 * another message rather than being one, and that message already wears the
 * result.
 */
export function toThreadRows(
  messages: Message[],
  {
    selfJid,
    resolveHandle,
    retractionWindowHours,
    t,
    now,
    resolvedShares,
    sharesFailed,
  }: ThreadRowsContext
): ThreadRow[] {
  return messages.flatMap((message): ThreadRow[] => {
    if (message.type === "event") {
      const notice = noticeText(message, resolveHandle, t)
      if (!notice) return []

      return [{ id: message.id, timestamp: message.timestamp, notice }]
    }

    if (message.type !== "chat" && message.type !== "tombstone") return []

    const share = message.type === "chat" ? message.share : undefined
    const attachment = message.type === "chat" ? message.attachment : undefined

    return [
      {
        id: message.id,
        body: message.body ?? "",
        timestamp: message.timestamp,
        isOwn: selfJid ? isOwnMessage(message, selfJid) : false,
        isRetracted: message.retracted,
        // Only a share message carries these — a plain row stays as it was.
        // A failed lookup is not "still resolving": it falls back to the link.
        ...(share && {
          share,
          resolvedShare: resolvedShares?.get(share.id),
          isShareResolving: resolvedShares === undefined && !sharesFailed,
          shareResolveFailed: sharesFailed === true,
        }),
        ...(attachment && { attachment }),
        // Only a live chat message carries a send status; a tombstone is history.
        status: message.type === "chat" ? message.status : undefined,
        author: message.author ? resolveHandle(message.author) : undefined,
        authorHandle: message.author,
        canRetract: canRetract(message, selfJid, retractionWindowHours, now),
      },
    ]
  })
}

/**
 * Phrased locally rather than showing the line Costanza sends: the server
 * knows people only as handles, and an opaque handle is not who added anybody
 * to a group. Its line is the fallback when the notice names no actor.
 */
function noticeText(
  event: EventMessage,
  resolveHandle: (handle: string) => PeerProfile,
  t: Translate
): string | undefined {
  const by = event.author && resolveHandle(event.author).displayName
  if (!by) return event.body

  switch (event.event) {
    case "archived":
      return String(t("chat.NOTICE_ARCHIVED", { by }))
    case "unarchived":
      return String(t("chat.NOTICE_UNARCHIVED", { by }))
    case "renamed":
      return String(t("chat.NOTICE_RENAMED", { by, name: event.name }))
    case "member_added":
      return String(
        t("chat.NOTICE_MEMBER_ADDED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
    case "member_removed":
      return String(
        t("chat.NOTICE_MEMBER_REMOVED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
    case "member_left":
      // Leaving names only the person who left, who is also the actor.
      return String(
        t("chat.NOTICE_MEMBER_LEFT", {
          member: resolveHandle(event.member).displayName,
        })
      )
    case "promoted":
      return String(
        t("chat.NOTICE_PROMOTED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
    case "demoted":
      return String(
        t("chat.NOTICE_DEMOTED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
  }
}
